import React, { Fragment } from "react";
import Component from "./Component";
import ScrollArea from "react-scrollbar";
import {disableBodyScroll, clearAllBodyScrollLocks} from 'body-scroll-lock';
import {CSSTransition} from "react-transition-group";
import ReactPortal from "./ReactPortal";

import { Dialog, Transition } from '@headlessui/react'


export default class Modal extends Component {

    static defaultProps = {
        canExit: true
    }
    targetElement = null;

    constructor(props) {
        super(props);

        this.scrollRef = React.createRef();
        this.noScrollRef = React.createRef();

        this.nodeRef = React.createRef();
        this.state = {
            isVisible: false,
            defaultScroll: props.defaultScroll,
            onCloseAction: props.onCloseAction,
            content: props.content,
            title: props.title,
            canExit: props.canExit,
            subtitle: props.subtitle
        }
    }

    componentDidMount() {
        let target = document.querySelector('#default-scrollable-open');
        if (target && (this.props.isVisible || this.state.isVisible)) {
            disableBodyScroll(target, {reserveScrollBarGap: true});
        }
        super.componentDidMount();
    }

    /**
     * Dismisses the modal when the click lands outside its panel.
     *
     * Opt-in per modal via `closeOnBackdrop`, not global: most callers here are
     * forms, and losing half-entered work to a stray click is a worse failure
     * than having to find the close button on a read-only panel.
     *
     * On mousedown rather than click, so selecting text inside the panel and
     * releasing outside it doesn't count as clicking away. `canExit` still
     * governs — a modal with no close button isn't meant to be dismissed.
     */
    handleBackdropMouseDown = (e) => {
        if (!this.state.closeOnBackdrop || !this.state.canExit) {
            return;
        }

        if (e.target?.closest?.('.modal-container')) {
            return;
        }

        this.hideModal();
    };

    async showModal(modalProps) {
        if (modalProps) {
            // Reset alongside `defaultScroll`: state persists between modals,
            // so an omitted flag has to mean off rather than "whatever the last
            // modal set".
            await this.promisedState({defaultScroll: false, closeOnBackdrop: false, ...modalProps});
        }
        await this.scrollTop()
        await this.promisedState({isVisible: true});

        // 3. Disable body scroll
        // if (document.querySelector('#default-scrollable')) {
        //     disableBodyScroll(document.querySelector('#default-scrollable'));
        // }
    }

    async hideModal(skipAction = false) {
        await this.promisedState({isVisible: false});

        // 4. Re-enable body scroll
        await TB.wait(300)

        if (!skipAction && this.props.onCloseAction) {
            await this.props.onCloseAction();
        }
    }

    scrollTop() {
        if (!this.scrollRef?.current?.scrollArea) {
            return;
        }

        return this.scrollRef.current.scrollArea.scrollTop();
    }

    async componentDidUpdate(prevProps, prevState, snapshot) {
        if ((!prevState.isVisible && this.state.isVisible) || (!prevProps.isVisible && this.props.isVisible)) {
            if (document.querySelector('#default-scrollable-open')) {
                disableBodyScroll(document.querySelector('#default-scrollable-open'), {reserveScrollBarGap: true});
            }
        }
        if ((!this.state.isVisible && prevState.isVisible) || (!this.props.isVisible && prevProps.isVisible)) {
            await TB.wait(700);
            if (document.querySelector('#default-scrollable-close') && !document.querySelectorAll('#default-scrollable-open').length) {
                clearAllBodyScrollLocks()
            }
        }
    }

    render() {

        let {
            isVisible,
            defaultScroll,
            modalClass,
        } = this.state;
        if (TB.isDefined(this.props.isVisible)) {
            isVisible = this.props.isVisible;
        }

        let modalClasses = {
            "modal-overlay": true,
            // "popped": isVisible
        };

        if (typeof this.props.modalClass === 'string') modalClasses[this.props.modalClass] = true;
        if (typeof modalClass === 'string') modalClasses[modalClass] = true;


        return (
            <ReactPortal wrapperId="react-portal-modal-container">
                <CSSTransition
                    in={isVisible}
                    timeout={{ entry: 0, exit: 500 }}
                    unmountOnExit
                    classNames="modal-overlay"
                    appear={true}
                    nodeRef={this.nodeRef}
                >
                    <div
                        className={TB.mapClass(modalClasses)}
                        ref={this.nodeRef}
                        onMouseDown={this.handleBackdropMouseDown}
                    >
                        {defaultScroll ? this.renderNoScroll(isVisible) : (
                            <ScrollArea
                                speed={0.8}
                                ref={this.scrollRef}
                                smoothScrolling={true}
                                contentClassName={"modal-scroll"}
                                stopScrollPropagation={!this.props.allowScrollPagination}
                                horizontal={false}>

                                {this.renderModalContent()}

                            </ScrollArea>
                        )}
                        </div>
                </CSSTransition>
            </ReactPortal>
        );
    }

    renderModalContent() {
        let {
            content,
            title,
            subtitle
        } = this.state;

        let {
            hideHeader
        } = this.props;

        // let Content = () => <React.Fragment>
        //     {content}
        // </React.Fragment>
        //
        // if (TB.isFunctionComponent(content)) {
        //     Content = content;
        // }

        return (
            <div className="modal-container">

                {!hideHeader && title && (
                    <h2 className="modal-title">
                        {title}
                    </h2>
                )}
                {!hideHeader && subtitle && (
                    <h4 className="modal-subtitle">
                        {subtitle}
                    </h4>
                )}

                {this.state.canExit && (
                    <span onClick={() => this.hideModal()}
                          className="close clickable">
                        <span>&times;</span>
                    </span>
                )}

                {content}
                {this.props.children}

            </div>
        )
    }

    renderNoScroll(isVisible) {
        return (
            <div className="defaultScroll" id={"default-scrollable" + (isVisible ? '-open' : '-close')} ref={ref => this.noScrollRef = ref}>
                <div className="scrollarea-content modal-scroll">
                    {this.renderModalContent()}
                </div>
            </div>
        )
    }

    componentWillUnmount() {
        // 5. Useful if we have called disableBodyScroll for multiple target elements,
        // and we just want a kill-switch to undo all that.
        // OR useful for if the `hideTargetElement()` function got circumvented eg. visitor
        // clicks a link which takes him/her to a different page within the app.
        clearAllBodyScrollLocks();
    }

}


export const ModalHeadless = ({ children, isVisible, onCloseAction, closeOnBackdrop = false }) => {
    return (
        <Transition appear show={isVisible} as={Fragment}>
            <Dialog static={!closeOnBackdrop} as="div" className="relative z-10" onClose={() => closeOnBackdrop && onCloseAction()}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-30"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center md:p-10">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            {/* <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"> */}
                            <Dialog.Panel className="w-full first-letter:transform overflow-hidden align-middle shadow-xl transition-all">

                                {children}

                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}